import {t} from 'ttag';
import React, {useState} from 'react';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  Grid2,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {Search} from '@mui/icons-material';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {useMask} from '@react-input/mask';
import {styles, TicketSelectorDialog, invoiceSearchValidationSchema} from '@invoice-system';

export const InvoiceSystemSearch = () => {
  const navigate = useNavigate();
  const inputTicketRef = useMask({mask: '___-__________', replacement: {_: /\d/}});
  const inputInvoiceRef = useMask({mask: '____/__________', replacement: {_: /\d/}});
  const cachedInvoice = sessionStorage.getItem('searchInvoice')?.match(/^\d{4}\/\d{10}$/)
    ? {type: 'invoice', value: sessionStorage.getItem('searchInvoice')} : null;
  const cachedSearch = sessionStorage.getItem('searchInvoice')?.match(/^(331|737)-\d{10}$/)
    ? {type: 'ticket', value: sessionStorage.getItem('searchInvoice')} : cachedInvoice;
  const [searchInvoice, setSearchInvoice] = useState<boolean>(cachedSearch?.type === 'invoice');
  const [ticketSelectorDialog, setTicketSelectorDialog] = useState<boolean>(false);

  const {control, handleSubmit, getValues, reset} = useForm({
    context: {searchInvoice: searchInvoice},
    resolver: yupResolver(invoiceSearchValidationSchema),
    defaultValues: {
      ticketNumber: cachedSearch?.type === 'ticket' ? cachedSearch?.value ?? '' : '',
      invoiceNumber: cachedSearch?.type === 'invoice' ? cachedSearch?.value ?? '' : ''
    }
  });

  const handleSubmitForm = handleSubmit(
    (data) => {
      if (searchInvoice && data?.invoiceNumber) {
        setTicketSelectorDialog(true);
      }
      if (!searchInvoice && data?.ticketNumber) {
        navigate(`/invoice/${data?.ticketNumber}`);
        sessionStorage.setItem('searchInvoice', data?.ticketNumber);
      }
    },
    (error) => console.error(error)
  );

  return (
    <Card elevation={3}>
      <CardHeader
        title={<Typography sx={styles.title}>{t`Invoice Search`}</Typography>}
        subheader={
          <Typography sx={styles.subHeader} variant={'subtitle2'}>
            {t`Search for a ticket/EMD number or invoice number to manage invoices.`}
          </Typography>
        }
      />
      <Divider />
      <CardContent sx={{py: 1}}>
        <RadioGroup
          sx={{pb: 0.5}}
          onChange={(e) => {
            reset({ticketNumber: '', invoiceNumber: ''});
            setSearchInvoice(e.target.value === 'true');
          }}
          value={searchInvoice}
          row
        >
          <FormControlLabel value={'false'} control={<Radio size={'small'} />} label={t`Ticket/EMD Number`} />
          <FormControlLabel value={'true'} control={<Radio size={'small'} />} label='Invoice Number' />
        </RadioGroup>
        <Grid2 container spacing={2}>
          {!searchInvoice ? (
            <Grid2 size={{xs: 12}}>
              <Controller
                name={'ticketNumber'}
                control={control}
                defaultValue={''}
                render={({field, fieldState: {error}}) => (
                  <TextField
                    {...field}
                    inputRef={inputTicketRef}
                    key={'inputTicketRef'}
                    slotProps={{
                      inputLabel: {
                        shrink: true
                      }
                    }}
                    id={'ticket-number-search'}
                    label={t`Ticket Number/EMD Number`}
                    placeholder={t`331-0123456789 or 737-0123456789`}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (value.length === 3 && !field.value?.includes('-')) {
                        value = `${value.slice(0, 3)}-`;
                      }
                      field.onChange(value);
                    }}
                    type={'text'}
                    helperText={!!error && error?.message}
                    error={!!error}
                    variant='outlined'
                    margin='normal'
                    fullWidth
                  />
                )}
              />
            </Grid2>
          ) : (
            <Grid2 size={{xs: 12}}>
              <Controller
                name={'invoiceNumber'}
                control={control}
                defaultValue={''}
                render={({field, fieldState: {error}}) => (
                  <TextField
                    {...field}
                    slotProps={{
                      inputLabel: {
                        shrink: true
                      }
                    }}
                    id={'invoice-number-search'}
                    label={t`Invoice Number`}
                    key={'inputInvoiceRef'}
                    inputRef={inputInvoiceRef}
                    placeholder={`YYYY/0123456789`}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (value.length === 4 && !field.value?.includes('/')) {
                        value = `${value.slice(0, 4)}/`;
                      }
                      field.onChange(value);
                    }}
                    type={'text'}
                    error={!!error}
                    helperText={!!error && error?.message}
                    variant='outlined'
                    margin='normal'
                    fullWidth
                  />
                )}
              />
            </Grid2>
          )}
        </Grid2>
      </CardContent>
      <Divider />
      <CardActions sx={styles.actions}>
        <Button variant='contained' color='primary' startIcon={<Search />} onClick={handleSubmitForm}>
          {searchInvoice ? t`Search Invoice` : t`Search Ticket`}
        </Button>
      </CardActions>
      {ticketSelectorDialog && searchInvoice && (
        <TicketSelectorDialog
          invoiceNumber={getValues('invoiceNumber') || ''}
          onClose={() => setTicketSelectorDialog(false)}
        />
      )}
    </Card>
  );
};
